// @flow

import gql from 'graphql-tag';

import { useQuery } from '@apollo/react-hooks';

import type { selectedApisQuery as selectedApisQueryType } from 'web-ssr/graphql/types';

type Props = {
  primaryAppSlug: string,
  secondaryAppSlug?: ?string,
};

type Result = {
  loading: *,
  fetchMore: *,
  error: *,
  data: selectedApisQueryType,
};

const selectedApisQuery = gql`
  query selectedApisQuery($appSlug: String!) {
    appBySlug(slug: $appSlug) {
      id
      currentImplementation {
        id
      }
    }
  }
`;

/**
 * When provided app slugs, this hook will make a query to the API to grab the
 * current implementation ID. This is useful for tracking user context when
 * a client is signing up. This allows us to understand where they came from.
 */
const useSelectedApis = ({ primaryAppSlug, secondaryAppSlug }: Props) => {
  const { data: primaryAppData }: Result = useQuery(selectedApisQuery, {
    variables: { appSlug: primaryAppSlug },
  });

  const { data: secondaryAppData }: Result = useQuery(selectedApisQuery, {
    variables: { appSlug: secondaryAppSlug },
    skip: !secondaryAppSlug,
  });

  const primaryAppId =
    primaryAppData &&
    primaryAppData.appBySlug &&
    primaryAppData.appBySlug.currentImplementation &&
    primaryAppData.appBySlug.currentImplementation.id;

  const secondaryAppId =
    secondaryAppData &&
    secondaryAppData.appBySlug &&
    secondaryAppData.appBySlug.currentImplementation &&
    secondaryAppData.appBySlug.currentImplementation.id;

  return [primaryAppId, secondaryAppId].filter(Boolean);
};

export default useSelectedApis;
